import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiGet, type Patient } from "../api/client";
import { PageHeader } from "../components/PageHeader";

/**
 * Historical compose message (`compose-message.html` / app.compose).
 * Archive is read-only — submit navigates to the patient conversation when known.
 */
export function ComposePage() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Patient | null>(null);
  const [body, setBody] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    apiGet<Array<{ patients: Patient[] }>>("/patients").then((d) =>
      setPatients(d?.[0]?.patients ?? []),
    );
  }, []);

  const hits = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term || selected) return [];
    return patients
      .filter((p) => p.name.toLowerCase().includes(term))
      .slice(0, 8);
  }, [patients, query, selected]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!selected || !body.trim()) return;
    setSent(true);
    // Prefer deep-link into the patient thread when id convention matches.
    const m = selected.id.match(/archive_patient_(\d+)/);
    const convId = m
      ? `archive_conversation_${m[1].padStart(2, "0")}`
      : "archive_conversation_01";
    navigate(`/app/patients/${selected.id}/conversations/${convId}`);
  }

  return (
    <>
      <PageHeader title="Compose message" />
      <div className="ne__compose composemsg" style={{ padding: "2.5vw 5%" }}>
        <div className="panel-body">
          <form name="composeForm" onSubmit={onSubmit}>
            <div className="input-group form-group" style={{ marginBottom: "1rem" }}>
              <span className="input-group-addon text-sm">To:</span>
              <input
                className="form-control ne__input"
                type="text"
                name="result"
                placeholder="Type patient name"
                autoComplete="off"
                value={selected ? selected.name : query}
                onChange={(e) => {
                  setSelected(null);
                  setQuery(e.target.value);
                  setSent(false);
                }}
                required
              />
            </div>
            {hits.length > 0 ? (
              <ul className="ne__typeahead" style={{ listStyle: "none", padding: 0 }}>
                {hits.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="ne__btn ne__btn--tertiary ne__btn--md"
                      onClick={() => {
                        setSelected(p);
                        setQuery(p.name);
                      }}
                    >
                      {p.name}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="mb-editor">
              <textarea
                className="form-control ne__textarea ne__textarea--primary"
                name="body"
                rows={6}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
                placeholder="Write a message…"
                style={{ width: "100%", minHeight: "8rem" }}
              />
              <br />
              <button
                className="btn btn-primary fr ne__btn ne__btn--primary"
                type="submit"
                disabled={!selected || !body.trim()}
              >
                Send
              </button>
              {sent ? (
                <p className="ne__empty-message" style={{ marginTop: "1rem" }}>
                  Message staged (archive is read-only).
                </p>
              ) : null}
            </div>
          </form>
          <p style={{ marginTop: "2rem" }}>
            <Link to="/app/messages">← Back to inbox</Link>
          </p>
        </div>
      </div>
    </>
  );
}
